import * as React from "react";
import {
  getCampaignById,
  getCampaignKeys,
  getCampaigns,
} from "@/apis/v1/campaign";
import { useQuery } from "@tanstack/react-query";
import { filterDataAccordingToAllowedAdvertisers } from "../../Advertiser/advertiser.validation";
import { CHANNELS, MODULES } from "@/constants";
import { validationSchema } from "./campaign.validation";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useUserContext } from "@/context/UserContext";
import dayjs, { Dayjs } from "dayjs";
import mapFilterQueryKeys from "@/utils/mapFilterQueryKeys";
import { omit } from "lodash";
import { useBeeswaxLineItem } from "../../Beeswax/beeswax.hook";
import { useLinkedinLineItem } from "../../Linkedin/linkedin.hook";
import { Channel, ReactQueryOptions } from "@/types";
import { useFacebookLineItem } from "../../LineItem/lineItem.hook";
import type {
  CampaignApiResponse,
  ChannelSwitchState,
  DisplayErrorResult,
  FacebookAudienceType,
} from "./campaign.interface";
import { useMergedEmailAudiences } from "../../Audience/EmailAudience/emailAudience.hook";
import { useRetargetAudienceList } from "../../Audience/SiteVisitorAudience/siteVisitorAudience.hook";
import {
  FACEBOOK_AUDIENCE_TYPE,
  CAMPAIGN_TYPE,
  CAMPAIGN_KEYS,
  LINE_ITEM_FORM_STATES,
} from ".././campaign.constant";
import convertDateToTimezone from "@/utils/convertDateToTimezone";
import { CAMPAIGN_TIMEZONE } from "./CreateOrEditCampaign/data";
import { validateDisplayLineItem } from "@/apis/v1/lineItem";
import {
  checkDisplayLineItemFormState,
  ERROR_FIELDS_TO_FORM_FIELDS,
  FREQUENCY_CAP_FIELDS_MAPPING,
} from "./DetailCampaign/data";

export function useCampaignKeysList(
  otherParams?: any,
  queryParams?: Record<string, any>
) {
  return useQuery(["campaigns", "keys"], getCampaignKeys, {
    ...otherParams,
    meta: {
      queryParams: {
        type: MODULES.CAMPAIGNS,
        ...queryParams,
      },
    },
  });
}

export const CAMPAIGNS_LIST_KEY = (
  keys?: Array<string | number | boolean | null>
) => [
  "campaigns",
  ...mapFilterQueryKeys(keys),
];

interface useCampaignsListArgs {
  queryParams: {
    module: string;
    advertiserId?: number | null;
    page?: number;
    size?: number;
    isPagination?: boolean;
    type: keyof typeof CAMPAIGN_TYPE | null;
    lean?: boolean;
    // Opt-in server-side name filter; empty string returns the full scoped list.
    search?: string;
  };
}
// `options` is an opt-in react-query passthrough (e.g. `enabled`) for callers that
// must gate the fetch — the meta/select wiring below always wins so a caller can't
// accidentally drop the allowed-advertiser filtering.
export function useCampaignsList(
  meta?: useCampaignsListArgs,
  options?: ReactQueryOptions
) {
  return useQuery(
    CAMPAIGNS_LIST_KEY(
      Object.values(omit(meta?.queryParams, "isPagination") || [])
    ),
    getCampaigns,
    {
      ...options,
      meta: {
        queryParams: meta?.queryParams,
      },
      select: filterDataAccordingToAllowedAdvertisers("advertiserId"),
    }
  );
}

export function useCampaign(
  id: number,
  args?: {
    queryParams?: Record<string, string>;
    options?: Record<any, any>;
  },
  key: keyof typeof CAMPAIGN_KEYS = "default"
) {
  const { queryParams = {}, options = {} } = args ?? {};

  return useQuery<CampaignApiResponse>(
    CAMPAIGN_KEYS[key]({ id, ...queryParams }),
    (context) => getCampaignById(id, context),
    {
      meta: {
        queryParams: { ...queryParams },
      },
      ...options,
    }
  );
}

export function useCreateOrEditForm(data?: any) {
  const defaultValues = useDefaultValues(data);
  const switchState = useSwitchState({
    isDisplay: Boolean(data?.["BeeswaxCampaign"]),
    isLinkedin: Boolean(data?.["LinkedinCampaign"]),
    isFacebook: Boolean(data?.["FacebookCampaign"]),
  });

  const campaignValidationSchema = validationSchema({
    display: switchState.display.state,
    linkedin: switchState.linkedin.state,
    facebook: switchState.facebook.state,
  });

  const rForm = useForm<any>({
    defaultValues,
    resolver: yupResolver(campaignValidationSchema),
  });

  return { rForm, switchState };
}

function useSwitchState(value: {
  isDisplay: boolean;
  isLinkedin: boolean;
  isFacebook: boolean;
}): ChannelSwitchState {
  const [displaySwitch, setDisplaySwitch] = React.useState<boolean>(
    value.isDisplay ?? false
  );
  const [linkedinSwitch, setLinkedinSwitch] = React.useState<boolean>(
    value.isLinkedin ?? false
  );
  const [facebookSwitch, setFacebookSwitch] = React.useState<boolean>(
    value.isFacebook ?? false
  );
  return {
    [CHANNELS.DISPLAY]: { state: displaySwitch, setState: setDisplaySwitch },
    [CHANNELS.LINKEDIN]: { state: linkedinSwitch, setState: setLinkedinSwitch },
    [CHANNELS.FACEBOOK]: { state: facebookSwitch, setState: setFacebookSwitch },
  };
}

function useDefaultValues(data?: any) {
  const { loggedInAdvertiserId } = useUserContext();
  if (data) {
    let editDefaultValues: any = {
      name: data?.name ?? "",
      advertiserCompanyId: data?.advertiserId ?? "",
      startDate: data?.startDate
        ? convertDateToNewYorkTimeZone(dayjs(data?.startDate).toDate())
        : null,
    };
    if (data["BeeswaxCampaign"]) {
      editDefaultValues = {
        ...editDefaultValues,
        [CHANNELS.DISPLAY]: {
          continents: data?.BeeswaxCampaign?.continent ?? "",
          budgetType: data?.BeeswaxCampaign?.budgetType ?? "",
          budgetValue: data?.BeeswaxCampaign?.budget ?? null,
        },
      };
    }
    if (data["LinkedinCampaign"]) {
      editDefaultValues = {
        ...editDefaultValues,
        [CHANNELS.LINKEDIN]: {
          objectiveTypeId: data?.LinkedinCampaign?.objectiveTypeId,
        },
      };
    }
    if (data["FacebookCampaign"]) {
      editDefaultValues = {
        ...editDefaultValues,
        [CHANNELS.FACEBOOK]: {
          objective: data?.FacebookCampaign?.objectiveType,
        },
      };
    }

    return editDefaultValues;
  }

  return {
    name: "",
    advertiserCompanyId: loggedInAdvertiserId
      ? loggedInAdvertiserId
      : data?.advertiserCompanyId
      ? data?.advertiserCompanyId
      : "",
    startDate: convertDateToNewYorkTimeZone(dayjs().add(1, "minute").toDate()),
  };
}

export function useLineItem({
  args,
  channel,
}: {
  args: { enabled: boolean; onSuccess: (data: any) => void; id: any };
  channel: Channel;
}) {
  return hook[channel](args);
}

export function useDisplayLineItemValidation({
  lineItemId,
  payload = {},
  options = {},
}: {
  lineItemId: string;
  payload: Record<string, any>;
  options?: ReactQueryOptions<
    unknown,
    unknown,
    unknown,
    string | Record<string, any>
  >;
}) {
  const {
    targetingExpression = null,
    creatives = null,
    ctvChannels = null,
    ...restPayload
  } = payload ?? {};
  return useQuery(
    ["line-item", "display", "validate", lineItemId, restPayload],
    (context) =>
      lineItemId &&
      validateDisplayLineItem(Number(lineItemId), payload, context),
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      ...options,
    }
  );
}

export function useAudienceForFacebookLineItem({
  advertiserId,
  audienceType,
}: {
  advertiserId: number;
  audienceType: FacebookAudienceType;
}) {
  const filterBySegmentId = (data: Record<string, any>) =>
    data.rows.filter((audience: Record<string, any>) =>
      Boolean(audience.segmentId)
    );
  const emailAudience = useMergedEmailAudiences(
    {
      queryParams: { channel: "facebook", advertiserId },
      options: {
        select: (data: Record<string, any>) => filterBySegmentId(data),
        enabled: Boolean(
          audienceType &&
            audienceType === FACEBOOK_AUDIENCE_TYPE.FacebookAudiences
        ),
      },
    },
    "withAdvertiserIdAndChannel"
  );
  const retargetAudience = useRetargetAudienceList({
    enabled: Boolean(
      audienceType &&
        audienceType === FACEBOOK_AUDIENCE_TYPE.FacebookRetargetAudiences
    ),
    advertiserId,
    options: {
      select: (data: Record<string, any>) => filterBySegmentId(data),
    },
  });

  const data = {
    [FACEBOOK_AUDIENCE_TYPE.FacebookAudiences]: emailAudience,
    [FACEBOOK_AUDIENCE_TYPE.FacebookRetargetAudiences]: retargetAudience,
  };

  if (audienceType) {
    return data[audienceType];
  }

  return { data: [], isLoading: false, isFetching: false };
}

const hook = {
  [CHANNELS.DISPLAY]: useBeeswaxLineItem,
  [CHANNELS.LINKEDIN]: useLinkedinLineItem,
  [CHANNELS.FACEBOOK]: useFacebookLineItem,
};

export function useCampaignChannelCardDisable(
  data: {
    display: {
      dspAccountId: string | null | undefined;
      campaign: Record<string, any> | undefined | null;
    };
    linkedin: {
      linkedinPageId: string | null | undefined;
      campaign: Record<string, any> | null | undefined;
    };
    facebook: {
      fbAdAccountId: string | null | undefined;
      campaign: Record<string, any> | undefined | null;
    };
  },
  isEdit: boolean
) {
  const disabledState = {
    isDisplayDisabled: true,
    isLinkedinDisabled: true,
    isFacebookDisabled: true,
  };

  if (data.display.dspAccountId) {
    if (isEdit && !data.display.campaign)
      disabledState.isDisplayDisabled = false;
    else disabledState.isDisplayDisabled = false;
  }
  if (data.linkedin.linkedinPageId) {
    if (isEdit && !data.linkedin.campaign)
      disabledState.isLinkedinDisabled = false;
    disabledState.isLinkedinDisabled = false;
  }
  if (data.facebook.fbAdAccountId) {
    if (isEdit && !data.facebook.campaign)
      disabledState.isFacebookDisabled = false;
    disabledState.isFacebookDisabled = false;
  }
  return disabledState;
}

export function useSetDisplayLineItemErrors({
  clearErrors,
  trigger,
  setError,
}: any) {
  const parseDisplayErrors = (
    errors: any,
    mapping: any
  ): DisplayErrorResult[] => {
    const results: DisplayErrorResult[] = [];

    function traverse(obj: any, path: string[] = []) {
      for (const key in obj) {
        if (key === "non_field_errors") continue;

        const currentPath = [...path, key];

        if (Array.isArray(obj[key])) {
          // Check if it's an array of error messages
          if (typeof obj[key][0] === "string") {
            const mappedKey = findMappedKey(currentPath, mapping);
            if (mappedKey) {
              obj[key].forEach((message: string) => {
                results.push({
                  field: mappedKey,
                  message: message,
                  path: currentPath,
                });
              });
            }
          } else {
            // Handle array of objects (like frequency_caps.limits)
            obj[key].forEach((item: any, index: number) => {
              // Special handling for frequency_caps
              if (path.includes("frequency_caps")) {
                for (const fieldKey in item) {
                  if (Array.isArray(item[fieldKey])) {
                    results.push({
                      field: fieldKey, // Original field name
                      message: item[fieldKey][0],
                      path: [...currentPath, index.toString(), fieldKey],
                    });
                  }
                }
              } else {
                traverse(item, [...currentPath, index.toString()]);
              }
            });
          }
        } else if (typeof obj[key] === "object" && obj[key] !== null) {
          traverse(obj[key], currentPath);
        }
      }
    }

    function findMappedKey(path: string[], mapping: any): string | null {
      let current = mapping;

      for (const key of path) {
        if (!current[key]) return null;
        if (typeof current[key] === "string") return current[key];
        current = current[key];
      }

      return null;
    }

    traverse(errors);
    return results;
  };
  const setDisplayError = React.useCallback(
    (
      data: {
        errors?: Record<string, any>;
        warnings?: Record<string, any>;
      },
      errors: Record<string, any>
    ) => {
      if (errors && Object.keys(errors).length > 0) {
        clearErrors();
      }
      if (data?.errors && Object.keys(data.errors).length > 0) {
        trigger().then(() => {
          const result = parseDisplayErrors(
            data.errors,
            ERROR_FIELDS_TO_FORM_FIELDS
          );
          result.forEach((error: DisplayErrorResult) => {
            if (error.path.includes("frequency_caps")) {
              const fieldName = `frequencyCap.0.${
                FREQUENCY_CAP_FIELDS_MAPPING[error.field]
              }`;

              setError(fieldName, {
                type: "manual",
                message: error.message,
              });
            }
            {
              setError(error.field, {
                type: "manual",
                message: error.message,
              });
            }
          });
        });
      }
    },
    []
  );
  return setDisplayError;
}

export function useLineItemStatus({
  lineItem,
  isLineItemDataFetching,
}: {
  lineItem: any;
  isLineItemDataFetching: boolean | undefined;
}) {
  const state = React.useMemo(() => {
    const activeState = {
      [LINE_ITEM_FORM_STATES.GENERAL]: false,
      [LINE_ITEM_FORM_STATES.TARGETING]: false,
      [LINE_ITEM_FORM_STATES.CREATIVES]: false,
    };
    if (lineItem) {
      activeState[LINE_ITEM_FORM_STATES.GENERAL] =
        checkDisplayLineItemFormState(LINE_ITEM_FORM_STATES.GENERAL, lineItem);
      activeState[LINE_ITEM_FORM_STATES.TARGETING] =
        checkDisplayLineItemFormState(
          LINE_ITEM_FORM_STATES.TARGETING,
          lineItem
        );
      activeState[LINE_ITEM_FORM_STATES.CREATIVES] =
        checkDisplayLineItemFormState(
          LINE_ITEM_FORM_STATES.CREATIVES,
          lineItem
        );
    }
    const isActiveAllowed = Object.values(activeState).every((value) => value);
    return { activeState, isActiveAllowed };
  }, [lineItem?.line_item_id, isLineItemDataFetching]);

  return state;
}

export function useLineItemFormToolTipValues(LineItemFormState: any) {
  const items = [
    {
      label: "General",
      id: LINE_ITEM_FORM_STATES.GENERAL,
      completed: LineItemFormState[LINE_ITEM_FORM_STATES.GENERAL],
    },
    {
      label: "Targeting",
      id: LINE_ITEM_FORM_STATES.TARGETING,
      completed: LineItemFormState[LINE_ITEM_FORM_STATES.TARGETING],
    },
    {
      label: "Creative",
      id: LINE_ITEM_FORM_STATES.CREATIVES,
      completed: LineItemFormState[LINE_ITEM_FORM_STATES.CREATIVES],
    },
  ];
  const no = items.reduce((acc, item) => (item.completed ? acc + 1 : acc), 0);
  return {
    title: `${no} of 3 completed to activate`,
    items,
  };
}

export function convertDateToNewYorkTimeZone(date: string | Date | Dayjs) {
  return convertDateToTimezone(date, CAMPAIGN_TIMEZONE);
}
